import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from "react-router-dom";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import "chartjs-adapter-moment";
import { Chart as ChartJS } from "chart.js";
import moment from 'moment';
import EventLocationController from './EventLocationController.js';
import EventHeaderNav from './EventHeaderNav.js';
import Spinner from "../../components/atoms/Spinner.js";
import { CounterCard, CounterSection } from '../Counter.js';
import { InfoIcon } from '../../components/atoms/Tooltip.js';

// Try to import zoom plugin, but don't break if it's not available
let zoomPlugin = null;
// try {
//   zoomPlugin = require("chartjs-plugin-zoom");
//   ChartJS.register(zoomPlugin);
// } catch (e) {
//   console.warn("chartjs-plugin-zoom not available - zoom functionality disabled");
// }

/*
 * Process floor data from API response using floorLayout config
 */
const processFloorData = (floorLayout, sensorData) => {
  if (!floorLayout || !floorLayout.floors || !Array.isArray(floorLayout.floors)) {
    console.warn('Invalid floorLayout config:', floorLayout);
    return [];
  }

  // Color palette - matches the chart colors
  const colors = [
    '#28a745', // Green
    '#007bff', // Blue
    '#6f42c1', // Purple
    '#fd7e14', // Orange
    '#e83e8c', // Pink
    '#20c997', // Teal
    '#ffc107', // Yellow
  ];

  return floorLayout.floors.map(floor => {
    // Safety check for throughputCounters
    if (!floor.throughputCounters || !Array.isArray(floor.throughputCounters)) {
      console.warn('Floor missing throughputCounters:', floor);
      return {
        floorNumber: floor.floorNumber || 0,
        floorLabel: floor.floorLabel || 'Unknown Floor',
        counters: []
      };
    }

    // Get current counts for each throughput counter
    const counters = floor.throughputCounters.map((counter, index) => {
      // Handle both string and numeric sensor IDs
      const sensorId = counter.sensorId;
      const sensorTimeSeries = sensorData[sensorId] || sensorData[String(sensorId)] || [];
      
      // Debug log if sensor is missing
    //   if (sensorTimeSeries.length === 0) {
    //     console.warn(`No data found for sensor ${sensorId} (${counter.displayLabel})`);
    //   }
      
      const latestData = sensorTimeSeries.length > 0 
        ? sensorTimeSeries[sensorTimeSeries.length - 1] 
        : { count: 0, countOut: 0 };

      // Get the value based on direction
      const value = counter.direction === 'in' 
        ? latestData.count || 0 
        : latestData.countOut || 0;

      return {
        displayLabel: counter.displayLabel,
        description: counter.description,
        value: value,
        sensorId: counter.sensorId,
        direction: counter.direction,
        color: colors[index % colors.length] // Add color that matches chart
      };
    });

    return {
      floorNumber: floor.floorNumber,
      floorLabel: floor.floorLabel,
      counters: counters
    };
  });
};

/**
 * Process floor chart data - creates time series for each throughput counter
 */
const processFloorChartData = (floor, sensorData) => {
  const datasets = [];
  
  // Safety check for floor and throughputCounters
  if (!floor || !floor.throughputCounters || !Array.isArray(floor.throughputCounters)) {
    console.warn('Invalid floor data or missing throughputCounters:', floor);
    return datasets;
  }
  
  // Color palette for different counters
  const colors = [
    '#28a745', // Green
    '#007bff', // Blue
    '#6f42c1', // Purple
    '#fd7e14', // Orange
    '#e83e8c', // Pink
    '#20c997', // Teal
    '#ffc107', // Yellow
  ];

  // Collect all timestamps to calculate totals
  const allTimestamps = new Set();
  const counterDataByTime = {}; // Store data by timestamp for total calculation

  floor.throughputCounters.forEach((counter, index) => {
    // Handle both string and numeric sensor IDs
    const sensorId = counter.sensorId;
    const sensorTimeSeries = sensorData[sensorId] || sensorData[String(sensorId)] || [];
    
    // Deduplicate by minute - keep most recent value per minute
    const dataByMinute = {};
    
    sensorTimeSeries
      .filter(item => item.timestamp)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .forEach(item => {
        const value = counter.direction === 'in' 
          ? item.count || 0 
          : item.countOut || 0;
        
        // Create a key for each minute (truncate to minute precision)
        const timestamp = new Date(item.timestamp);
        timestamp.setSeconds(0, 0); // Zero out seconds and milliseconds
        const minuteKey = timestamp.getTime();
        
        // Keep overwriting with latest value for this minute
        dataByMinute[minuteKey] = {
          timestamp: timestamp,
          value: value
        };
      });
    
    // Convert the deduplicated data to chart format
    const dataPoints = Object.entries(dataByMinute)
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .map(([minuteKey, data]) => {
        const timestamp = parseInt(minuteKey);
        allTimestamps.add(timestamp);
        
        // Store value for total calculation
        if (!counterDataByTime[timestamp]) {
          counterDataByTime[timestamp] = {};
        }
        counterDataByTime[timestamp][index] = data.value;
        
        return {
          x: data.timestamp,
          y: data.value
        };
      });

    // Only add dataset if we have data points
    if (dataPoints.length > 0) {
      datasets.push({
        label: counter.displayLabel,
        data: dataPoints,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        fill: false,
        pointRadius: 2,
        borderWidth: 2,
        borderDash: [5, 5], // Dashed line for individual sources
        tension: 0.4,
      });
    }
  });

  // Calculate and add the total line
  if (datasets.length > 0) {
    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);
    
    // Track last known value for each counter
    const lastKnownValues = {};
    
    const totalDataPoints = sortedTimestamps.map(timestamp => {
      // Update last known values for counters that have data at this timestamp
      const countersAtTime = counterDataByTime[timestamp] || {};
      Object.entries(countersAtTime).forEach(([counterIndex, value]) => {
        lastKnownValues[counterIndex] = value;
      });
      
      // Sum all last known values (carry forward previous values if no new data)
      let total = 0;
      Object.values(lastKnownValues).forEach(value => {
        total += value;
      });
      
      return {
        x: new Date(timestamp),
        y: total
      };
    });

    // Add total line as SOLID line (main metric)
    datasets.push({
      label: `${floor.floorLabel} Total`,
      data: totalDataPoints,
      borderColor: '#e20074',
      backgroundColor: '#e20074',
      fill: false,
      pointRadius: 2.5,
      borderWidth: 2.5,
      tension: 0.4,
    });
  }

  return datasets;
};

// Total Traffic Section - Unified summary and chart
const TotalTrafficSection = ({ regularDayCount, regularDayCountOut, doorData, isToday, locationData, floorsData }) => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({ datasets: [] });
  const chartRef = useRef(null);

  // Use totalSummary label if available, otherwise default
  const summaryLabel = locationData?.floorLayout?.totalSummary?.label || "Total Event Traffic";

  // Process chart data
  useEffect(() => {
    setLoading(true);

    if (!doorData || doorData.length === 0) {
      setChartData({ datasets: [] });
      setLoading(false);
      return;
    }

    // Process door data for Chart.js format
    const processedData = doorData
      .filter(item => item.timestamp)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .map((item) => {
        const timestamp = new Date(item.timestamp);
        return {
          x: timestamp,
          inTraffic: item.count || 0,
          outTraffic: item.countOut || 0,
        };
      });

    const datasets = [
      {
        label: 'Total Entries In',
        data: processedData.map(item => ({ x: item.x, y: item.inTraffic })),
        borderColor: '#28a745',
        backgroundColor: '#28a745',
        fill: false,
        pointRadius: 2,
        borderWidth: 3,
        tension: 0.4,
      },
      {
        label: 'Total Entries Out',
        data: processedData.map(item => ({ x: item.x, y: item.outTraffic })),
        borderColor: '#dc3545',
        backgroundColor: '#dc3545',
        fill: false,
        pointRadius: 2,
        borderWidth: 3,
        tension: 0.4,
      }
    ];

    setChartData({ datasets });
    setLoading(false);
  }, [doorData]);

  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt')?.trim() || '#333';

  const maxValue = Math.max(
    ...chartData.datasets.flatMap(dataset => dataset.data.map(item => item.y))
  ) || 100;

  const chartOptions = {
    scales: {
      x: {
        title: {
          color: primaryTextColor,
          display: true,
          text: "Time",
          padding: { top: 0, bottom: 10 },
        },
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
          tooltipFormat: 'h:mm A',
          parser: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          source: "auto",
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          callback: function(value, index, ticks) {
            const date = new Date(value);
            return date.toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          }
        },
      },
      y: {
        display: true,
        title: {
          color: primaryTextColor,
          display: true,
          text: "People",
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: true,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          stepSize: maxValue < 100 ? 20 : 100,
          callback: function (value) {
            if (maxValue < 100) {
              if (value % 20 === 0) return value;
            } else {
              if (value % 100 === 0) return value;
            }
          },
        },
        max: maxValue < 100 
          ? Math.max(100, Math.ceil(maxValue / 20) * 20 + 20)
          : Math.max(Math.ceil(maxValue / 100) * 100 + 100)
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: false,
        animation: {
          duration: 0,
        },
        callbacks: {
          title: (context) => {
            if (context.length === 0) return "";
            const xValue = context[0].raw.x;
            if (xValue) {
              const formattedTime = xValue.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              });
              return formattedTime;
            }
            return "Invalid time";
          },
          label: (context) => ` ${context.raw.y} people`,
        },
      },
      legend: {
        display: true,
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
      zoom: zoomPlugin ? {
        limits: {
          x: { min: 'original', max: 'original', minRange: 60 * 60 * 1000 },
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      } : undefined,
    },
  };

  return (
    <CounterSection 
      title={summaryLabel}
      height="auto"
    >
      {/* Summary Information */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "40px",
          padding: "40px 60px",
          background: "#fff",
          borderRadius: "20px",
          width: "100%",
        }}
      >
        {/* Left Column - Floor Totals */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "stretch",
            borderRadius: "16px",
            padding: "24px 32px",
            // More intense, even shadow
            boxShadow:
              "0 8px 24px rgba(0,0,0,0.15), 0 -4px 12px rgba(0,0,0,0.05), 4px 0 12px rgba(0,0,0,0.06), -4px 0 12px rgba(0,0,0,0.06)",
          }}
        >
          <div style={{ fontSize: "20px", fontWeight: "600", color: "#333", marginBottom: "16px" }}>
            Total Floor Entries
            <InfoIcon 
              tooltip={"Break down of how many people have entered each floor."}
              position='top'
              width={'250px'}
              size="18px"
              color="#64748b"
              backgroundColor="#1e293b"
              textColor="#ffffff"
              fontSize="18px"
            />
          </div>

          {floorsData && floorsData.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                flexGrow: 1,
                gap: "12px",
              }}
            >
              {floorsData.map((floor, index) => {
                const floorTotal = floor.counters.reduce((sum, counter) => sum + counter.value, 0);
                return (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "#f9f9f9",
                      borderRadius: "10px",
                      padding: "12px 16px",
                      boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div style={{ fontSize: "20px", color: "#555", fontWeight: "500" }}>
                      {floor.floorLabel}
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: "600", color: "#E20074" }}>
                      {floorTotal.toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                color: "#888",
                fontStyle: "italic",
                marginTop: "40px",
              }}
            >
              No floor data available
            </div>
          )}
        </div>

        {/* Right Column - Totals */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "stretch",
            gap: "40px",
          }}
        >
          {/* Total In */}
          <div
            style={{
              flex: 1,
              borderRadius: "16px",
              padding: "40px",
              textAlign: "center",
              // Deeper, all-sided shadow
              boxShadow:
                "0 10px 28px rgba(0,0,0,0.18), 0 -6px 16px rgba(0,0,0,0.06), 6px 0 14px rgba(0,0,0,0.08), -6px 0 14px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div style={{ fontSize: "20px", color: "#555", marginBottom: "8px", letterSpacing: "0.3px" }}>
              Total Entries In
              <InfoIcon 
                tooltip={"People that have entered Club Magenta from the main entrance."}
                position='top'
                width={'250px'}
                size="18px"
                color="#64748b"
                backgroundColor="#1e293b"
                textColor="#ffffff"
                fontSize="18px"
              />
            </div>
            <div style={{ fontSize: "48px", fontWeight: "700", color: "#E20074", lineHeight: "1.1" }}>
              {regularDayCount.toLocaleString()}
            </div>
          </div>

          {/* Total Out */}
          <div
            style={{
              flex: 1,
              borderRadius: "16px",
              padding: "40px",
              textAlign: "center",
              // Matching deeper shadow
              boxShadow:
                "0 10px 28px rgba(0,0,0,0.18), 0 -6px 16px rgba(0,0,0,0.06), 6px 0 14px rgba(0,0,0,0.08), -6px 0 14px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div style={{ fontSize: "20px", color: "#555", marginBottom: "8px", letterSpacing: "0.3px" }}>
              Total Entries Out
              <InfoIcon 
                tooltip={"People that have left Club Magenta."}
                position='top'
                width={'250px'}
                size="18px"
                color="#64748b"
                backgroundColor="#1e293b"
                textColor="#ffffff"
                fontSize="18px"
              />
            </div>
            <div style={{ fontSize: "48px", fontWeight: "700", color: "#E20074", lineHeight: "1.1" }}>
              {regularDayCountOut.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div style={{
        marginTop: "16px",
        paddingTop: "24px",
        borderTop: "1px solid #e0e0e0"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          paddingLeft: "20px",
          paddingRight: "20px"
        }}>
          {/* <p style={{ fontSize: "20px", margin: 0, color: "#e20074", fontWeight: "500" }}>
            Traffic Over Time
          </p> */}
          {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {zoomPlugin && (
              <>
                <button
                  style={{ marginRight: "5px" }}
                  className={"btn border-1"}
                  onClick={() => chartRef.current?.zoom(1.25)}
                >
                  +
                </button>
                <button
                  style={{ marginRight: "5px" }}
                  className={"btn border-1"}
                  onClick={() => chartRef.current?.zoom(0.75)}
                >
                  -
                </button>
                <button
                  className={"btn border-1"}
                  onClick={() => chartRef.current?.resetZoom()}
                >
                  Reset
                </button>
              </>
            )}
          </div> */}
        </div>

        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '300px',
            color: '#666',
            fontSize: '16px'
          }}>
            Loading chart...
          </div>
        ) : !doorData || doorData.length === 0 ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '300px',
            color: '#666',
            fontSize: '16px'
          }}>
            No chart data available for the selected date
          </div>
        ) : (
          <div style={{ position: 'relative', height: '300px', padding: "0 20px" }}>
            <Line ref={chartRef} data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
    </CounterSection>
  );
};

// Floor Section Component - Unified counter cards and chart
const FloorSection = ({ floorData, floorConfig, sensorData, isToday }) => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({ datasets: [] });
  const chartRef = useRef(null);

  // Process chart data
  useEffect(() => {
    setLoading(true);

    if (!floorConfig || !sensorData) {
    //   console.warn('FloorSection: Missing floor config or sensorData', { floorConfig, sensorData });
      setChartData({ datasets: [] });
      setLoading(false);
      return;
    }

    const datasets = processFloorChartData(floorConfig, sensorData);
    setChartData({ datasets });
    setLoading(false);
  }, [floorConfig, sensorData]);

  // Safety check for required props - AFTER hooks
  if (!floorData || !floorConfig) {
    // console.warn('FloorSection: Missing required data', { floorData, floorConfig });
    return null;
  }
  
  // Calculate total by summing all counter values
  const floorTotal = floorData.counters.reduce((sum, counter) => sum + counter.value, 0);

  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt')?.trim() || '#333';

  const maxValue = Math.max(
    ...chartData.datasets.flatMap(dataset => dataset.data.map(item => item.y))
  ) || 100;

  const chartOptions = {
    scales: {
      x: {
        title: {
          color: primaryTextColor,
          display: true,
          text: "Time",
          padding: { top: 0, bottom: 10 },
        },
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
          tooltipFormat: 'h:mm A',
          parser: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          source: "auto",
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          callback: function(value, index, ticks) {
            const date = new Date(value);
            return date.toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          }
        },
      },
      y: {
        display: true,
        title: {
          color: primaryTextColor,
          display: true,
          text: "People",
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: true,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          stepSize: maxValue < 100 ? 20 : 100,
          callback: function (value) {
            if (maxValue < 100) {
              if (value % 20 === 0) return value;
            } else {
              if (value % 100 === 0) return value;
            }
          },
        },
        max: maxValue < 100 
          ? Math.max(100, Math.ceil(maxValue / 20) * 20 + 20)
          : Math.max(Math.ceil(maxValue / 100) * 100 + 100)
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: false,
        animation: {
          duration: 0,
        },
        callbacks: {
          title: (context) => {
            if (context.length === 0) return "";
            const xValue = context[0].raw.x;
            if (xValue) {
              const formattedTime = xValue.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              });
              return formattedTime;
            }
            return "Invalid time";
          },
          label: (context) => ` ${context.raw.y} people`,
        },
      },
      legend: {
        display: true,
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
      zoom: zoomPlugin ? {
        limits: {
          x: { min: 'original', max: 'original', minRange: 60 * 60 * 1000 },
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      } : undefined,
    },
  };
  
  return (
    <CounterSection 
      title={floorData.floorLabel}
      height="auto"
    >
      {/* Counter Cards - Grid Layout Inspired by Total Section */}
      <div style={{
        display: "grid",
        gridTemplateColumns: floorData.counters.length === 1 
          ? "1fr 1fr" 
          : floorData.counters.length === 2
          ? "1fr 1fr 1fr"
          : floorData.counters.length === 3
          ? "1fr 1fr 1fr 1fr"
          : "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "30px",
        padding: "40px 60px",
        background: "#fff",
        borderRadius: "20px",
        width: "100%",
      }}>
        {/* Individual Counter Cards */}
        {floorData.counters.map((counter, index) => (
          <CounterCard
            key={index}
            title={counter.displayLabel}
            description={counter.description}
            count={counter.value}
            subtitle={isToday ? "Live Count" : "Final Count"}
            borderColor={counter.color}
            minWidth={200}
          />
        ))}
        
        {/* Floor Total Card - Stands out like Total In/Out */}
        <div
          style={{
            borderRadius: "16px",
            padding: "32px 24px",
            textAlign: "center",
            boxShadow: "0 10px 28px rgba(0,0,0,0.18), 0 -6px 16px rgba(0,0,0,0.06), 6px 0 14px rgba(0,0,0,0.08), -6px 0 14px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            background: "#fff",
          }}
        >
          <div style={{ 
            fontSize: "18px", 
            color: "#555", 
            marginBottom: "12px", 
            letterSpacing: "0.3px",
            fontWeight: "500"
          }}>
            {floorData.floorLabel} Total Entries
          </div>
          <div style={{ 
            fontSize: "42px", 
            fontWeight: "700", 
            color: "#E20074", 
            lineHeight: "1.1"
          }}>
            {floorTotal.toLocaleString()}
          </div>
          <div style={{ 
            fontSize: "16px", 
            color: "#666",
            fontWeight: "400",
            marginTop: "8px"
          }}>
            {isToday ? "Live Total" : "Final Total"}
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div style={{
        marginTop: "24px",
        paddingTop: "24px",
        borderTop: "1px solid #e0e0e0"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          paddingLeft: "20px",
          paddingRight: "20px"
        }}>
          {/* <p style={{ fontSize: "20px", margin: 0, color: "#e20074", fontWeight: "500" }}>
            Traffic Over Time
          </p> */}
          {/* <div style={{ display: "flex", alignItems: "center", gap: "8px"}}>
            {zoomPlugin && (
              <>
                <button
                  style={{ marginRight: "5px" }}
                  className={"btn border-1"}
                  onClick={() => chartRef.current?.zoom(1.25)}
                >
                  +
                </button>
                <button
                  style={{ marginRight: "5px" }}
                  className={"btn border-1"}
                  onClick={() => chartRef.current?.zoom(0.75)}
                >
                  -
                </button>
                <button
                  className={"btn border-1"}
                  onClick={() => chartRef.current?.resetZoom()}
                >
                  Reset
                </button>
              </>
            )}
          </div> */}
        </div>

        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '300px',
            color: '#666',
            fontSize: '16px'
          }}>
            Loading chart...
          </div>
        ) : !chartData.datasets || chartData.datasets.length === 0 || 
            chartData.datasets.every(ds => !ds.data || ds.data.length === 0) ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '300px',
            color: '#666',
            fontSize: '16px'
          }}>
            No chart data available for this floor
          </div>
        ) : (
          <div style={{ position: 'relative', height: '300px', padding: "0 20px" }}>
            <Line ref={chartRef} data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
    </CounterSection>
  );
};

export default function EventLocation() {
  const location = useLocation();
  
  // Extract location data from React Router state
  const locationData = location.state?.currentLocation;
  const locationId = location.state?.id;

  return (
    <EventLocationController locationData={locationData} locationId={locationId}>
      {({ 
        loading, 
        regularDayCount,
        regularDayCountOut, 
        doorData,
        sensorData,
        isToday, 
        autoFetchEnabled, 
        setAutoFetchEnabled, 
        authCtx,
        refreshData,
        selectedDate,
        setSelectedDate,
        minDate,
        maxDate,
        enabledDates,
        showCalendar,
        setShowCalendar
      }) => {
        // Debug: Log the data structure
        // console.log('locationData:', locationData);
        // console.log('floorLayout:', locationData?.floorLayout);
        // console.log('sensorData:', sensorData);
        
        // Process floor data using the floorLayout config
        const floorLayout = locationData?.floorLayout;
        const floorsData = processFloorData(floorLayout, sensorData);
        
        // Get total summary label
        const totalSummaryLabel = floorLayout?.totalSummary?.label || "Total Event Traffic";

        return (
          <>
            {authCtx.loggedIn ? (
              <>
                {loading ? (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100vw',
                    height: '100vh'
                  }}>
                    <Spinner size="large" />
                  </div>
                ) : (
                  <>
                    {/* Header Section */}
                    <div className="sticky-top pt-3" style={{ backgroundColor: '#ffffff' }}>
                      <div className="px-3">
                        <EventHeaderNav
                          locationData={locationData}
                          loading={loading}
                          isToday={isToday}
                          onRefresh={refreshData}
                          selectedDate={selectedDate}
                          setSelectedDate={setSelectedDate}
                          minDate={minDate}
                          maxDate={maxDate}
                          enabledDates={enabledDates}
                          showCalendar={showCalendar}
                          setShowCalendar={setShowCalendar}
                        />
                      </div>
                      <hr />
                    </div>

                    <div className="container-fluid" style={{ padding: "20px" }}>

                      {/* Total Traffic Section */}
                      <TotalTrafficSection 
                        regularDayCount={regularDayCount} 
                        regularDayCountOut={regularDayCountOut}
                        doorData={doorData}
                        isToday={isToday}
                        locationData={locationData}
                        floorsData={floorsData}
                      />

                      {/* Floor Sections */}
                      {floorLayout && floorLayout.floors && floorsData && floorsData.length > 0 && floorsData.map((floorData, index) => {
                        const floorConfig = floorLayout.floors[index];
                        
                        // Skip if floorConfig is missing
                        if (!floorConfig) {
                          console.warn(`Missing floorConfig for floor index ${index}`);
                          return null;
                        }
                        
                        return (
                          <FloorSection
                            key={floorData.floorNumber}
                            floorData={floorData}
                            floorConfig={floorConfig}
                            sensorData={sensorData || {}}
                            isToday={isToday}
                          />
                        );
                      })}
                      
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="p-4">
                <Link to={"/auth"}>
                  Not Authorized. Please Log-In with given credentials or contact support.
                </Link>
              </div>
            )}
          </>
        );
      }}
    </EventLocationController>
  );
}